import type { ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { answersOwnerTurn, type MeetingRoomRow } from '../../../lib/meeting-room-rows';
import { TaskLoaderMark } from '../../chat/TaskLoaderMark';

// Shown while a turn is in flight but there's no thinking step to surface yet (the
// gap before the first step, or a tool with no thinkingMessage): the looping Orion
// task mark with a generic "Thinking…" label. Shares ThinkingLine's layout + shimmer
// so the swap to the first real step is seamless. The label agrees with the assistant's
// gender — Hebrew "חושב" (m) vs "חושבת" (f); only he/hi actually differ (chat.thinking.female).
export function ThinkingLoaderOnly({ aside }: { aside?: ReactNode } = {}) {
  const { t } = useTranslation();
  const { gender } = useAgentName();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingInlineStart: 2 }}>
      <TaskLoaderMark />
      <span
        className="orion-think-line"
        style={{ fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 'var(--sym-weight-medium)', letterSpacing: '0.01em' }}
      >
        {t(gender === 'female' ? 'chat.thinking.female' : 'chat.thinking')}
      </span>
      {aside}
    </div>
  );
}

// The live "what Orion is doing right now" line — the latest THINKING step of the
// in-flight turn, one at a time, with a left-to-right shimmer so it reads as active.
export function ThinkingLine({ text, aside }: { text: string; aside?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingInlineStart: 2 }}>
      <TaskLoaderMark />
      <span
        key={text}
        className="orion-think-line"
        style={{ fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 'var(--sym-weight-medium)', letterSpacing: '0.01em' }}
      >
        {text}
      </span>
      {aside}
    </div>
  );
}

/** A pack's opening greeting must not stand down the walkthrough's progress line. */
export function ChatTurnProgress({
  inFlight,
  lastMessage,
  packPending,
  connectPending,
  surveyWriting,
  thinking,
  connectThinking,
  packThinking,
  aside,
}: {
  inFlight: boolean;
  lastMessage?: MeetingRoomRow;
  packPending: boolean;
  connectPending: boolean;
  surveyWriting?: boolean;
  thinking?: string | null;
  connectThinking?: string | null;
  packThinking: string;
  /** Beside the line: the chat's "Preview site" link while the agent works on a Symphony site (`SitePreviewLink`). */
  aside?: ReactNode;
}) {
  if (!inFlight) return null;
  // A real reply carries its own progress, except while a separate card/connection is still owed.
  if (answersOwnerTurn(lastMessage) && !packPending && !connectPending && !surveyWriting) return null;
  if (thinking) return <ThinkingLine text={thinking} aside={aside} />;
  if (connectThinking) return <ThinkingLine text={connectThinking} aside={aside} />;
  if (packPending) return <ThinkingLine text={packThinking} aside={aside} />;
  return <ThinkingLoaderOnly aside={aside} />;
}

// A thread-shaped shimmer skeleton — the loading state echoes what's about to
// arrive (a user bubble on the right, assistant prose lines on the left).
export function ConversationLoader({ floating }: { floating: boolean }) {
  const userBubble = (width: string) => (
    <div className="orion-shimmer" style={{ alignSelf: 'flex-end', width, height: 44, borderRadius: 'var(--sym-radius-chat-bubble)' }} />
  );
  const assistantLines = (widths: string[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {widths.map((w, i) => (
        <div key={i} className="orion-shimmer" style={{ width: w, height: 13, borderRadius: 6 }} />
      ))}
    </div>
  );
  return (
    <div
      aria-hidden
      style={{
        minHeight: floating ? 220 : undefined,
        width: '100%',
        maxWidth: 720,
        margin: '0 auto',
        padding: floating ? '20px 4px' : '28px 4px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 26,
        pointerEvents: 'none',
      }}
    >
      {userBubble('42%')}
      {assistantLines(['88%', '76%', '48%'])}
      {!floating && userBubble('28%')}
      {!floating && assistantLines(['70%', '40%'])}
    </div>
  );
}

// Lives in `lib/use-typewriter.ts` so a unit test can load it without this file's dependency graph.
export { useTypewriter, type TypewriterPace } from '../../../lib/use-typewriter';
